import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, Navigate } from 'react-router-dom';
import { miabApi, type MiabSurvey, type MiabResultQuestion } from '../api/miab';
import { useAuth } from '../hooks/useAuth';
import { showToast } from '../components/Toast';
import { copyToClipboard } from '../lib/clipboard';
import MiabSurveyEditor from '../components/MiabSurveyEditor';

function fmtDate(s: string | null): string {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return s;
  }
}

const STATE_LABEL: Record<string, string> = { upcoming: 'Upcoming', open: 'Open', closed: 'Closed' };

/**
 * Admin view: the MIAB survey calendar plus live results for whichever survey
 * is selected. Results refresh on an interval so Jeremy and Heather can watch
 * responses land during the open week.
 */
export default function MiabAdminPage() {
  const { user, isLoading } = useAuth();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [editing, setEditing] = useState<'new' | number | null>(null);

  const surveysQuery = useQuery({
    queryKey: ['miab', 'admin', 'surveys'],
    queryFn: miabApi.adminSurveys,
    enabled: user?.role === 'admin',
  });

  const surveys = surveysQuery.data?.surveys ?? [];
  const activeId = selectedId ?? surveys.find((s) => s.state === 'open')?.id ?? surveys[0]?.id ?? null;

  const resultsQuery = useQuery({
    queryKey: ['miab', 'admin', 'results', activeId],
    queryFn: () => miabApi.results(activeId!),
    enabled: activeId != null,
    refetchInterval: 30_000,
  });

  const editingSurvey =
    typeof editing === 'number' ? surveys.find((s) => s.id === editing) ?? null : null;

  // The editor needs the survey's current questions; /survey/:slug returns them
  // and lets admins see drafts.
  const editQuery = useQuery({
    queryKey: ['miab', 'edit', editingSurvey?.slug],
    queryFn: () => miabApi.bySlug(editingSurvey!.slug),
    enabled: Boolean(editingSurvey),
  });

  if (isLoading) {
    return (
      <div className="auth-loading">
        <div className="spinner" />
        <span>Loading…</span>
      </div>
    );
  }
  if (user?.role !== 'admin') return <Navigate to="/" replace />;

  const results = resultsQuery.data;
  const shareLink = (s: MiabSurvey) => `${window.location.origin}/miab/${s.slug}`;

  return (
    <div className="miab-page">
      <header className="miab-header">
        <Link to="/" className="miab-back">← Dashboard</Link>
        <h1>Marketing in a Box — Surveys</h1>
        <p className="miab-intro">
          Franchisees answer the survey for each box. Copy a survey's link into your
          announcement email; recipients sign in and land straight on it.
        </p>
      </header>

      {editing !== null ? (
        editing === 'new' ? (
          <MiabSurveyEditor survey={null} questions={[]} locked={false}
            onDone={() => setEditing(null)} onCancel={() => setEditing(null)} />
        ) : editQuery.isLoading ? (
          <p>Loading survey…</p>
        ) : (
          <MiabSurveyEditor
            survey={editingSurvey}
            questions={editQuery.data?.questions ?? []}
            locked={(editingSurvey?.response_count ?? 0) > 0}
            onDone={() => setEditing(null)}
            onCancel={() => setEditing(null)}
          />
        )
      ) : (
        <div className="miab-admin-layout">
          <aside className="miab-survey-list">
            <button className="btn btn-primary miab-new-btn" onClick={() => setEditing('new')}>
              + New survey
            </button>
            {surveysQuery.isLoading && <p>Loading surveys…</p>}
            {surveys.map((s) => (
              <button
                key={s.id}
                className={`miab-survey-row${s.id === activeId ? ' is-active' : ''}`}
                onClick={() => setSelectedId(s.id)}
              >
                <span className="miab-survey-row-title">{s.box_label}</span>
                <span className={`miab-state miab-state-${s.state}`}>{STATE_LABEL[s.state]}</span>
                <span className="miab-survey-row-sub">
                  {fmtDate(s.opens_at)} – {fmtDate(s.closes_at)}{s.is_published ? '' : ' · draft'}
                </span>
                <span className="miab-survey-row-sub">
                  {s.question_count ?? 0} questions · {s.response_count ?? 0} responses
                </span>
              </button>
            ))}
          </aside>

          <section className="miab-results">
            {resultsQuery.isLoading && <p>Loading results…</p>}
            {results && (
              <>
                <div className="miab-results-head">
                  <div>
                    <h2>{results.survey.title}</h2>
                    <p className="miab-results-sub">
                      <strong>{results.responses}</strong> response{results.responses === 1 ? '' : 's'}
                    </p>
                  </div>
                  <div className="miab-results-actions">
                    <Link to={`/miab/${results.survey.slug}?preview=1`} className="btn">Preview as franchisee</Link>
                    <button className="btn" onClick={() => setEditing(results.survey.id)}>Edit survey</button>
                    <button className="btn" onClick={async () => { await copyToClipboard(shareLink(results.survey)); showToast('Survey link copied.'); }}>
                      Copy survey link
                    </button>
                  </div>
                </div>

                {results.responses === 0 && <p className="miab-notice">No responses yet.</p>}

                <div className="miab-results-questions">
                  {results.results.map((q, i) => (
                    <ResultQuestion key={q.id} q={q} index={i + 1} responses={results.responses} />
                  ))}
                </div>
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

function ResultQuestion({ q, index, responses }: { q: MiabResultQuestion; index: number; responses: number }) {
  const max = q.rating_max ?? 5;
  // Rank rating rows by average, choices by pick count.
  const ranked = [...q.options].sort((a, b) =>
    q.qtype === 'rating' ? (b.avgRating ?? 0) - (a.avgRating ?? 0) : b.picks - a.picks,
  );

  return (
    <div className="miab-result-q">
      <h3><span className="miab-q-num">{index}.</span> {q.prompt}</h3>

      {q.qtype === 'rating' && (
        <ol className="miab-tally">
          {ranked.map((o) => (
            <li key={o.id} className="miab-tally-row">
              {o.image_url && <img src={o.image_url} alt="" loading="lazy" />}
              <span className="miab-tally-name">{o.label}</span>
              <span className="miab-tally-bar">
                <span style={{ width: `${o.avgRating != null ? (o.avgRating / max) * 100 : 0}%` }} />
              </span>
              <span className="miab-tally-votes">
                {o.avgRating != null ? o.avgRating.toFixed(1) : '—'}
                <small> /{max} · {o.ratingCount}</small>
              </span>
            </li>
          ))}
        </ol>
      )}

      {(q.qtype === 'single' || q.qtype === 'multi') && (
        <ol className="miab-tally">
          {ranked.map((o) => {
            const pct = responses > 0 ? Math.round((o.picks / responses) * 100) : 0;
            return (
              <li key={o.id} className="miab-tally-row">
                <span className="miab-tally-name">{o.label}</span>
                <span className="miab-tally-bar"><span style={{ width: `${pct}%` }} /></span>
                <span className="miab-tally-votes">{o.picks} ({pct}%)</span>
              </li>
            );
          })}
        </ol>
      )}

      {q.qtype === 'text' && (
        <div className="miab-text-answers">
          {(q.textAnswers?.length ?? 0) === 0 ? (
            <p className="miab-results-sub">No responses.</p>
          ) : (
            q.textAnswers!.map((t, k) => (
              <blockquote key={k} className="miab-text-answer">
                <span className="miab-text-af">{t.af}</span>
                <p>{t.text}</p>
              </blockquote>
            ))
          )}
        </div>
      )}
    </div>
  );
}
